import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";
import { TIERS, startCheckout } from "@/data/tiers";

const formatPrice = (n) => {
  // Show 2 decimals only if non-integer (e.g. $32.40 — required for 10% off annual)
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

const Pricing = () => {
  const [billing, setBilling] = useState("monthly"); // 'monthly' | 'annual'
  const [checkingOut, setCheckingOut] = useState(null); // tier id in flight

  const onSubscribe = async (tierId) => {
    setCheckingOut(tierId);
    try {
      await startCheckout(tierId, billing);
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setCheckingOut(null);
    }
  };

  return (
    <main data-testid="pricing-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO
        title="Membership"
        description="Vaymilo membership — Insider, Studio, and Founder tiers. All-access digital products, AI kits, and templates."
        path="/pricing"
      />
      <PromoBanner />
      <Header />

      {/* Hero */}
      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="overline">— Membership</div>
          <h1
            data-testid="pricing-headline"
            className="mt-5 font-sora text-[44px] sm:text-[60px] lg:text-[80px] font-extrabold tracking-tight leading-[1.02] text-[#111111]"
          >
            Pick your level.
            <br className="hidden sm:block" />
            Cancel anytime.
          </h1>
          <p className="mt-6 max-w-2xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70">
            Three tiers, no surprises. Studio is the most popular — it unlocks
            the entire library plus every monthly drop. Founder is capped at
            100 members. Insider is for supporters who just want in.
          </p>

          {/* Billing toggle */}
          <div
            data-testid="pricing-toggle"
            className="mt-10 inline-flex items-center gap-1 bg-white border border-[#E7E7E2] rounded-full p-1"
          >
            {["monthly", "annual"].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBilling(mode)}
                data-testid={`pricing-toggle-${mode}`}
                className={`font-manrope text-[13px] font-medium px-4 py-2 rounded-full transition-colors ${
                  billing === mode
                    ? "bg-[#111111] text-white"
                    : "text-[#111111]/65 hover:text-[#111111]"
                }`}
              >
                {mode === "monthly" ? "Monthly" : "Annual · 10% off"}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <section className="bg-[#F7F7F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {TIERS.map((tier) => {
              const price =
                billing === "monthly" ? tier.priceMonthly : tier.priceAnnual;
              const period = billing === "monthly" ? "/month" : "/year";
              const isFeatured = !!tier.featured;
              const isLoading = checkingOut === tier.id;
              return (
                <div
                  key={tier.id}
                  data-testid={`pricing-card-${tier.id}`}
                  className={`relative bg-white rounded-2xl p-7 md:p-8 flex flex-col ${
                    isFeatured
                      ? "border-2 border-[#2563EB] shadow-[0_24px_60px_-24px_rgba(37,99,235,0.35)]"
                      : "border border-[#E7E7E2]"
                  }`}
                >
                  {isFeatured && (
                    <span className="absolute -top-3 left-7 inline-flex items-center gap-1 bg-[#2563EB] text-white font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full">
                      <Sparkles size={11} strokeWidth={2.5} />
                      Most popular
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-3">
                    <h3
                      data-testid={`tier-name-${tier.id}`}
                      className="font-sora text-[24px] font-bold text-[#111111] tracking-tight"
                    >
                      {tier.name}
                    </h3>
                    {tier.capped && (
                      <span className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-[#111111]/55">
                        {tier.capped}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
                    {tier.tagline}
                  </p>

                  <div className="mt-7 flex items-baseline gap-2">
                    <span
                      data-testid={`tier-price-${tier.id}`}
                      className="font-sora text-[44px] font-extrabold text-[#111111] tracking-tight leading-none"
                    >
                      {formatPrice(price)}
                    </span>
                    <span className="font-manrope text-[14px] text-[#111111]/55">
                      {period}
                    </span>
                  </div>
                  {billing === "annual" && (
                    <p className="mt-1 font-mono text-[11px] tracking-[0.16em] uppercase text-[#2563EB]">
                      Save 10% vs. monthly
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => onSubscribe(tier.id)}
                    disabled={checkingOut !== null}
                    data-testid={`tier-cta-${tier.id}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 font-manrope font-medium text-[14.5px] px-5 py-3.5 rounded-lg transition-colors disabled:opacity-60 ${
                      isFeatured
                        ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                        : "bg-[#111111] hover:bg-[#2563EB] text-white"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        Opening checkout…
                        <Loader2 size={14} strokeWidth={2.25} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        Subscribe to {tier.name}
                        <ArrowUpRight size={14} strokeWidth={2.25} />
                      </>
                    )}
                  </button>

                  <ul className="mt-8 pt-6 border-t border-[#E7E7E2] space-y-3">
                    {tier.perks.map((p) => (
                      <li
                        key={p}
                        className="flex items-baseline gap-2.5 font-manrope text-[14px] text-[#111111]/85 leading-relaxed"
                      >
                        <Check
                          size={13}
                          strokeWidth={2.5}
                          className="mt-[3px] shrink-0 text-[#2563EB]"
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Already a member */}
          <div className="mt-14 text-center font-manrope text-[14px] text-[#111111]/65">
            Already a member?{" "}
            <Link
              to="/login"
              data-testid="pricing-link-login"
              className="text-[#111111] hover:text-[#2563EB] border-b border-[#E7E7E2] hover:border-[#2563EB] pb-0.5"
            >
              Sign in →
            </Link>
          </div>
        </div>
      </section>

      {/* Hybrid model — individual products still available */}
      <section className="bg-white border-y border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="overline">— Prefer one-off?</div>
              <h2 className="mt-5 font-sora text-[32px] md:text-[44px] font-bold text-[#111111] tracking-tight leading-[1.05]">
                You can also buy any kit on its own.
              </h2>
              <p className="mt-5 max-w-xl font-manrope text-[16px] md:text-[17px] text-[#111111]/65 leading-relaxed">
                Don&rsquo;t want a membership? Every kit is sold individually
                too — pick the one you need, pay once. No subscription required.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <Link
                to="/products"
                data-testid="pricing-browse-products"
                className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg border border-[#111111] hover:bg-[#111111] hover:text-white transition-colors"
              >
                Browse individual kits
                <ArrowUpRight size={16} strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Pricing;
